/**
 * Google authorization for data access (not login).
 *
 * We use Google Identity Services' OAuth code client to run a one-time
 * "Connect Gmail" flow with `access_type: 'offline'` + `prompt: 'consent'`,
 * which yields a refresh token. That code is exchanged server-side (the
 * `connectGoogleAccount` callable) and the refresh token is stored there —
 * every `scanInboxes` call mints its own fresh access token from it, so the
 * user is never re-prompted on a per-scan basis.
 */

const GMAIL_SCOPE = 'https://www.googleapis.com/auth/gmail.readonly';
const GIS_SRC = 'https://accounts.google.com/gsi/client';

interface CodeResponse {
  code?: string;
  error?: string;
  error_description?: string;
}
interface CodeClient {
  requestCode: () => void;
}
interface GoogleOAuth2 {
  initCodeClient: (config: {
    client_id: string;
    scope: string;
    ux_mode?: string;
    access_type?: string;
    prompt?: string;
    callback: (resp: CodeResponse) => void;
    error_callback?: (err: { type?: string; message?: string }) => void;
  }) => CodeClient;
}

declare global {
  interface Window {
    google?: { accounts?: { oauth2?: GoogleOAuth2 } };
  }
}

let gisPromise: Promise<void> | null = null;

/** Lazy-load the Google Identity Services script once. */
function loadGis(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (gisPromise) return gisPromise;
  gisPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services.'));
    document.head.appendChild(script);
  });
  return gisPromise;
}

/**
 * Prompt the user (via a Google popup) to authorize read-only Gmail access,
 * once, with offline access — returns the one-time authorization code and the
 * page origin the flow ran on (Google validates the code against it).
 */
export async function requestGmailAuthCode(): Promise<{ code: string; redirectUri: string }> {
  const clientId = (import.meta.env as Record<string, string | undefined>)
    .VITE_GOOGLE_OAUTH_CLIENT_ID;
  if (!clientId) {
    throw new Error('Gmail is not configured (missing VITE_GOOGLE_OAUTH_CLIENT_ID).');
  }

  await loadGis();
  const oauth2 = window.google?.accounts?.oauth2;
  if (!oauth2) throw new Error('Google Identity Services is unavailable.');

  const code = await new Promise<string>((resolve, reject) => {
    const client = oauth2.initCodeClient({
      client_id: clientId,
      scope: GMAIL_SCOPE,
      ux_mode: 'popup',
      access_type: 'offline',
      // Forces the consent screen (and therefore a refresh token) every time,
      // rather than only on first-ever grant.
      prompt: 'consent',
      callback: (resp) => {
        if (resp.code) resolve(resp.code);
        else reject(new Error(resp.error_description || resp.error || 'Authorization cancelled.'));
      },
      error_callback: (err) => reject(new Error(err.message || 'Authorization failed.')),
    });
    client.requestCode();
  });

  return { code, redirectUri: window.location.origin };
}
